import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listGroupConfigurationsByClassId } from "../../api/groupConfigurationApi";
import { listStudents } from "../../api/studentApi";
import { listStaff } from "../../api/staffApi";
import { getDueList } from "../../api/feeApi";
import { listExamConfigs, listSubjectConfigs, listExamResults, getExamMarkGrid } from "../../api/examApi";
import useAuth from "../../hooks/useAuth";
import { isPlatformOperator } from "../../utils/authUtils";
import {
  listSmsTemplates,
  createSmsTemplate,
  updateSmsTemplate,
  deleteSmsTemplate,
  listAttendanceSmsTemplates,
  createAttendanceSmsTemplate,
  deleteAttendanceSmsTemplate,
  getSmsBalance,
  getAllSmsBalances,
  giveSmsBalance,
  sendManyToManySms,
  sendSmsToStaff,
  sendSmsToStudents,
  sendCustomSms,
  sendSmsToAllStudents,
  absentStudentList,
  sendAbsentSms,
  getSmsLog,
  getBalanceGivenLog,
} from "../../api/smsApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const subjectApi = createLookupApi("subjects");
const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * SMS management — folds digicampusbd's 16 separate SMS pages (Send Option's
 * 9 audience-specific send screens, 4 Report views, Create Template, and
 * Attendance's own SMS Template page) into one, tab-switched, matching the
 * backend's own consolidation (see SmsSendingService/SmsReportService
 * javadoc). SMS Purchase (buying credit via a payment gateway) isn't built —
 * only the super-admin manual balance grant is (see SmsBalanceService
 * javadoc) — so the Balance tab shows/grants balance but has no "buy more"
 * flow.
 *
 * Found while sweeping the SMS module (see MIGRATION.md): the original 5
 * audiences (students/staff/custom/manual/absent) covered
 * classwise/section-wise/institutewise/absent-wise/dynamic/dynamic-bulk, but
 * silently dropped 3 real audience-specific flows whose whole point is
 * auto-populating each selected student's message from real backend data
 * instead of the staff member typing it by hand: `send-sms-to-student-duewise`
 * (real due-amount SMS, direct port of `StudentFeeDueSmsService`'s message
 * format), `send-sms-to-student-examresult` (per-student exam result
 * summary), and `send-sms-to-student-exam-result-subject-wise` (per-subject
 * marks) — all three confirmed by reading their real templates directly, all
 * three reuse the same already-built many-to-many custom-send endpoint the
 * "Custom Message Per Student" panel already uses, so no backend change was
 * needed — just wiring the already-existing due/exam-result/exam-mark report
 * endpoints into 3 new auto-populating panels.
 */
export default function SmsPage() {
  const [tab, setTab] = useState("send");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  return (
    <MainLayout>
      <div className="page-header">
        <h1>SMS Management</h1>
      </div>
      <Tabs
        tabs={[
          { key: "send", label: "Send SMS" },
          { key: "templates", label: "Templates" },
          { key: "balance", label: "Balance" },
          { key: "reports", label: "Reports" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "send" && <SendTab setError={setError} setSuccess={setSuccess} />}
      {tab === "templates" && <TemplatesTab setError={setError} setSuccess={setSuccess} />}
      {tab === "balance" && <BalanceTab setError={setError} setSuccess={setSuccess} />}
      {tab === "reports" && <ReportsTab setError={setError} />}
    </MainLayout>
  );
}

function SendTab({ setError, setSuccess }) {
  const [audience, setAudience] = useState("students");
  return (
    <>
      <div className="attendance-filters">
        <label>
          Audience
          <select value={audience} onChange={(e) => setAudience(e.target.value)}>
            <option value="students">Students (class / section / institute wide)</option>
            <option value="staff">Staff</option>
            <option value="custom">Custom Message Per Student</option>
            <option value="manual">Manual (raw numbers)</option>
            <option value="absent">Absent Students</option>
            <option value="due">Fee Due (auto-message)</option>
            <option value="examresult">Exam Result (auto-message)</option>
            <option value="examresult-subject">Exam Result — Subject Wise (auto-message)</option>
          </select>
        </label>
      </div>
      {audience === "students" && <StudentsSendPanel setError={setError} setSuccess={setSuccess} />}
      {audience === "staff" && <StaffSendPanel setError={setError} setSuccess={setSuccess} />}
      {audience === "custom" && <CustomSendPanel setError={setError} setSuccess={setSuccess} />}
      {audience === "manual" && <ManualSendPanel setError={setError} setSuccess={setSuccess} />}
      {audience === "absent" && <AbsentSendPanel setError={setError} setSuccess={setSuccess} />}
      {audience === "due" && <DueSendPanel setError={setError} setSuccess={setSuccess} />}
      {audience === "examresult" && <ExamResultSendPanel setError={setError} setSuccess={setSuccess} />}
      {audience === "examresult-subject" && <ExamResultSubjectSendPanel setError={setError} setSuccess={setSuccess} />}
    </>
  );
}

function useStudentPicker() {
  const [academicYears, setAcademicYears] = useState([]);
  const [classConfigs, setClassConfigs] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    Promise.all([academicYearApi.list(), listClassConfigurations()]).then(([years, configs]) => {
      setAcademicYears(years);
      setClassConfigs(configs);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    if (!academicYear) return;
    listStudents(academicYear, classConfigurationId || undefined).then((list) => {
      setStudents(list);
      setSelectedIds([]);
    });
  }, [academicYear, classConfigurationId]);

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(selectedIds.length === students.length ? [] : students.map((s) => s.identificationId));

  return { academicYears, classConfigs, academicYear, setAcademicYear, classConfigurationId, setClassConfigurationId, students, selectedIds, toggle, toggleAll };
}

function StudentsSendPanel({ setError, setSuccess }) {
  const p = useStudentPicker();
  const [message, setMessage] = useState("");
  const [wholeInstitute, setWholeInstitute] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setError("");
    if (!message.trim()) {
      setError("Enter a message.");
      return;
    }
    setSending(true);
    try {
      let res;
      if (wholeInstitute) {
        res = await sendSmsToAllStudents({ academicYear: p.academicYear, message });
      } else {
        if (p.selectedIds.length === 0) {
          setError("Select at least one student, or check 'whole institute'.");
          setSending(false);
          return;
        }
        res = await sendSmsToStudents({ identificationIds: p.selectedIds, message });
      }
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={p.academicYear} onChange={(e) => p.setAcademicYear(e.target.value)}>
            {p.academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class (blank = all classes)
          <select value={p.classConfigurationId} onChange={(e) => p.setClassConfigurationId(e.target.value)} disabled={wholeInstitute}>
            <option value="">All classes</option>
            {p.classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label className="checkbox-label">
          <input type="checkbox" checked={wholeInstitute} onChange={(e) => setWholeInstitute(e.target.checked)} />
          Send to every active student this year (skip selection below)
        </label>
      </div>

      {!wholeInstitute && (
        <DataTable
          columns={[
            {
              key: "select",
              label: <input type="checkbox" checked={p.students.length > 0 && p.selectedIds.length === p.students.length} onChange={p.toggleAll} />,
              render: (s) => <input type="checkbox" checked={p.selectedIds.includes(s.identificationId)} onChange={() => p.toggle(s.identificationId)} />,
            },
            { key: "studentRoll", label: "Roll" },
            { key: "studentName", label: "Student" },
            { key: "customStudentId", label: "Student ID" },
          ]}
          rows={p.students}
        />
      )}

      <label>Message</label>
      <textarea rows={3} style={{ width: "100%" }} value={message} onChange={(e) => setMessage(e.target.value)} />
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send SMS"}
        </button>
      </div>
    </>
  );
}

function StaffSendPanel({ setError, setSuccess }) {
  const [staff, setStaff] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    listStaff().then(setStaff);
  }, []);

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(selectedIds.length === staff.length ? [] : staff.map((s) => s.staffId));

  const handleSend = async () => {
    setError("");
    if (selectedIds.length === 0 || !message.trim()) {
      setError("Select at least one staff member and enter a message.");
      return;
    }
    setSending(true);
    try {
      const res = await sendSmsToStaff({ staffIds: selectedIds, message });
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <DataTable
        columns={[
          {
            key: "select",
            label: <input type="checkbox" checked={staff.length > 0 && selectedIds.length === staff.length} onChange={toggleAll} />,
            render: (s) => <input type="checkbox" checked={selectedIds.includes(s.staffId)} onChange={() => toggle(s.staffId)} />,
          },
          { key: "customStaffId", label: "Staff ID" },
          { key: "staffName", label: "Name" },
          { key: "designationName", label: "Designation" },
          { key: "mobileNumber", label: "Mobile" },
        ]}
        rows={staff}
      />
      <label>Message</label>
      <textarea rows={3} style={{ width: "100%" }} value={message} onChange={(e) => setMessage(e.target.value)} />
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send SMS"}
        </button>
      </div>
    </>
  );
}

function CustomSendPanel({ setError, setSuccess }) {
  const p = useStudentPicker();
  const [messages, setMessages] = useState({});
  const [sending, setSending] = useState(false);

  const setMsg = (id, val) => setMessages((prev) => ({ ...prev, [id]: val }));

  const handleSend = async () => {
    setError("");
    const lines = p.selectedIds.map((id) => ({ identificationId: id, message: messages[id] || "" })).filter((l) => l.message.trim());
    if (lines.length === 0) {
      setError("Select students and enter a message for at least one.");
      return;
    }
    setSending(true);
    try {
      const res = await sendCustomSms({ lines });
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={p.academicYear} onChange={(e) => p.setAcademicYear(e.target.value)}>
            {p.academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select value={p.classConfigurationId} onChange={(e) => p.setClassConfigurationId(e.target.value)}>
            <option value="">All classes</option>
            {p.classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
      </div>
      <DataTable
        columns={[
          {
            key: "select",
            label: "",
            render: (s) => <input type="checkbox" checked={p.selectedIds.includes(s.identificationId)} onChange={() => p.toggle(s.identificationId)} />,
          },
          { key: "studentName", label: "Student" },
          { key: "customStudentId", label: "Student ID" },
          {
            key: "message",
            label: "Message",
            render: (s) => (
              <input
                style={{ width: "100%" }}
                disabled={!p.selectedIds.includes(s.identificationId)}
                value={messages[s.identificationId] || ""}
                onChange={(e) => setMsg(s.identificationId, e.target.value)}
              />
            ),
          },
        ]}
        rows={p.students}
      />
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send SMS"}
        </button>
      </div>
    </>
  );
}

function ManualSendPanel({ setError, setSuccess }) {
  const [lines, setLines] = useState([{ key: Math.random(), to: "", message: "" }]);
  const [sending, setSending] = useState(false);

  const setLine = (key, field, value) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  const addLine = () => setLines((prev) => [...prev, { key: Math.random(), to: "", message: "" }]);
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const handleSend = async () => {
    setError("");
    const usable = lines.filter((l) => l.to.trim() && l.message.trim());
    if (usable.length === 0) {
      setError("Add at least one number and message.");
      return;
    }
    setSending(true);
    try {
      const res = await sendManyToManySms({ lines: usable.map((l) => ({ to: l.to, message: l.message })) });
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
      setLines([{ key: Math.random(), to: "", message: "" }]);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="table-scroll">
      <table className="attendance-table">
        <thead>
          <tr>
            <th>Phone Number</th>
            <th>Message</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key}>
              <td>
                <input value={l.to} onChange={(e) => setLine(l.key, "to", e.target.value)} placeholder="01XXXXXXXXX" />
              </td>
              <td>
                <input style={{ width: "100%" }} value={l.message} onChange={(e) => setLine(l.key, "message", e.target.value)} />
              </td>
              <td>
                {lines.length > 1 && (
                  <button type="button" className="btn-link danger" onClick={() => removeLine(l.key)}>
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="form-actions" style={{ justifyContent: "space-between" }}>
        <button type="button" className="btn-link" onClick={addLine}>
          + Add Line
        </button>
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send SMS"}
        </button>
      </div>
    </>
  );
}

function AbsentSendPanel({ setError, setSuccess }) {
  const [academicYears, setAcademicYears] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [shiftId, setShiftId] = useState("");
  const [attendanceDate, setAttendanceDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const shiftApi = createLookupApi("shifts");

  useEffect(() => {
    Promise.all([academicYearApi.list(), shiftApi.list()]).then(([years, shiftList]) => {
      setAcademicYears(years);
      setShifts(shiftList);
      if (years.length > 0) setAcademicYear(years[0].name);
      if (shiftList.length > 0) setShiftId(String(shiftList[0].id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    if (!academicYear || !shiftId || !attendanceDate) return;
    setLoading(true);
    absentStudentList(academicYear, attendanceDate, shiftId)
      .then((list) => {
        setRows(list);
        setSelectedIds([]);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [academicYear, shiftId, attendanceDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(selectedIds.length === rows.length ? [] : rows.map((r) => r.attendanceId));

  const handleSend = async () => {
    setError("");
    if (selectedIds.length === 0) {
      setError("Select at least one absent student.");
      return;
    }
    setSending(true);
    try {
      const res = await sendAbsentSms({ attendanceIds: selectedIds, message: message.trim() || undefined });
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Shift
          <select value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date
          <input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} />
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            {
              key: "select",
              label: <input type="checkbox" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleAll} />,
              render: (r) => <input type="checkbox" checked={selectedIds.includes(r.attendanceId)} onChange={() => toggle(r.attendanceId)} />,
            },
            { key: "studentRoll", label: "Roll" },
            { key: "studentName", label: "Student" },
            { key: "classConfigName", label: "Class" },
            { key: "periodName", label: "Period" },
          ]}
          rows={rows}
          emptyMessage="No un-notified absences found for this date."
        />
      )}

      <label>Custom Message (optional — leave blank to auto-generate from the Absent template)</label>
      <textarea rows={2} style={{ width: "100%" }} value={message} onChange={(e) => setMessage(e.target.value)} />
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send Absent SMS"}
        </button>
      </div>
    </>
  );
}

/**
 * Direct port of `send-sms-to-student-duewise.html`'s real message format
 * (`StudentFeeDueSmsService`, confirmed by reading the template directly):
 * pick Year + Section, list every student's real total due (already-built
 * `GET /api/fee-reports/due`), auto-fill an individualized message for every
 * student with a due above 0, and send them all via the many-to-many
 * custom-send endpoint — same real "select at least one item with total fee
 * more than 0" rule (only due>0 rows get a checkbox at all here).
 */
function DueSendPanel({ setError, setSuccess }) {
  const { user } = useAuth();
  const instituteName = user && user.instituteName;
  const [academicYears, setAcademicYears] = useState([]);
  const [classConfigs, setClassConfigs] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([academicYearApi.list(), listClassConfigurations()]).then(([years, configs]) => {
      setAcademicYears(years);
      setClassConfigs(configs);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  const load = () => {
    if (!academicYear || !classConfigurationId) {
      setRows([]);
      return;
    }
    setLoading(true);
    getDueList(academicYear, classConfigurationId)
      .then((list) => {
        setRows(list);
        setSelectedIds([]);
      })
      .catch(() => setError("Failed to load due list."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [academicYear, classConfigurationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const dueRows = rows.filter((r) => r.totalDue > 0);
  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(selectedIds.length === dueRows.length ? [] : dueRows.map((r) => r.identificationId));

  const handleSend = async () => {
    setError("");
    if (selectedIds.length === 0) {
      setError("Select at least one student with a due amount above 0.");
      return;
    }
    setSending(true);
    try {
      const lines = dueRows
        .filter((r) => selectedIds.includes(r.identificationId))
        .map((r) => ({
          identificationId: r.identificationId,
          message: `This is to inform that your Student ID: ${r.customStudentId}, Name: ${r.studentName}, Payable fees Tk ${r.totalDue} from ${instituteName}`,
        }));
      const res = await sendCustomSms({ lines });
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            <option value="">Select Section</option>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            {
              key: "select",
              label: <input type="checkbox" checked={dueRows.length > 0 && selectedIds.length === dueRows.length} onChange={toggleAll} />,
              render: (r) =>
                r.totalDue > 0 ? (
                  <input type="checkbox" checked={selectedIds.includes(r.identificationId)} onChange={() => toggle(r.identificationId)} />
                ) : null,
            },
            { key: "studentRoll", label: "Roll" },
            { key: "studentName", label: "Student" },
            { key: "customStudentId", label: "Student ID" },
            { key: "details", label: "Due Details" },
            { key: "totalDue", label: "Total Due" },
          ]}
          rows={rows}
          emptyMessage="No students found for this section/year."
        />
      )}

      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send Due SMS"}
        </button>
      </div>
    </>
  );
}

/**
 * Direct port of `send-sms-to-student-examresult.html`'s real message format
 * (confirmed by reading the template directly): pick Year + Section + Exam,
 * list every real result for that section (already-built `GET
 * /api/exam-results?examConfigId=`, filtered client-side the same way
 * `ExamSubjectWiseGradePage` filters its own already-built list), auto-fill
 * each student's Exam/Name/ID/Class/Roll/Total Marks/GPA message, send via
 * many-to-many.
 */
function ExamResultSendPanel({ setError, setSuccess }) {
  const { user } = useAuth();
  const instituteName = user && user.instituteName;
  const [academicYears, setAcademicYears] = useState([]);
  const [classConfigs, setClassConfigs] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([academicYearApi.list(), listClassConfigurations()]).then(([years, configs]) => {
      setAcademicYears(years);
      setClassConfigs(configs);
      if (years.length > 0) setAcademicYear(years[0].name);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) {
      setExamConfigs([]);
      return;
    }
    listExamConfigs(cc.classId).then((list) => {
      setExamConfigs(list);
      setExamConfigId(list.length > 0 ? String(list[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  const examName = (examConfigs.find((e) => String(e.examConfigId) === examConfigId) || {}).examName || "";
  const sectionName = (classConfigs.find((c) => String(c.id) === classConfigurationId) || {}).classConfigName || "";

  const load = () => {
    if (!examConfigId || !classConfigurationId || !academicYear) {
      setRows([]);
      return;
    }
    setLoading(true);
    listExamResults(examConfigId)
      .then((list) => {
        setRows(list.filter((r) => String(r.classConfigurationId) === classConfigurationId && r.academicYear === academicYear));
        setSelectedIds([]);
      })
      .catch(() => setError("Failed to load exam results."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [examConfigId, classConfigurationId, academicYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(selectedIds.length === rows.length ? [] : rows.map((r) => r.identificationId));

  const handleSend = async () => {
    setError("");
    if (selectedIds.length === 0) {
      setError("Select at least one student.");
      return;
    }
    setSending(true);
    try {
      const lines = rows
        .filter((r) => selectedIds.includes(r.identificationId))
        .map((r) => ({
          identificationId: r.identificationId,
          message: `Exam Name: ${examName}, Name: ${r.studentName}, ID: ${r.customStudentId}, Class: ${sectionName}, Roll: ${r.studentRoll}, Total Marks: ${r.obtainMark}, GPA: ${r.avgGpaWithOptional}, From: ${instituteName}`,
        }));
      const res = await sendCustomSms({ lines });
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            <option value="">Select Exam</option>
            {examConfigs.map((e) => (
              <option key={e.examConfigId} value={e.examConfigId}>
                {e.examName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            {
              key: "select",
              label: <input type="checkbox" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleAll} />,
              render: (r) => <input type="checkbox" checked={selectedIds.includes(r.identificationId)} onChange={() => toggle(r.identificationId)} />,
            },
            { key: "studentRoll", label: "Roll" },
            { key: "studentName", label: "Student" },
            { key: "customStudentId", label: "Student ID" },
            { key: "obtainMark", label: "Total Marks" },
            { key: "letterGrade", label: "Grade" },
            { key: "avgGpaWithOptional", label: "GPA" },
            { key: "resultStatus", label: "Status" },
          ]}
          rows={rows}
          emptyMessage="No exam results found for this exam/section/year."
        />
      )}

      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send Result SMS"}
        </button>
      </div>
    </>
  );
}

/**
 * Direct port of `send-sms-to-student-exam-result-subject-wise.html`'s real
 * message format (confirmed by reading the template directly): the exact
 * same Year/Section/Exam/Group/Subject filters as `ExamMarkEntryPage` (same
 * already-built `GET /api/exam-marks` grid), auto-fill each student's
 * Name/Class/Roll/Exam/Subject/Obtained Marks/Grade message, send via
 * many-to-many.
 */
function ExamResultSubjectSendPanel({ setError, setSuccess }) {
  const { user } = useAuth();
  const instituteName = user && user.instituteName;
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [subjectConfigs, setSubjectConfigs] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [subjectNames, setSubjectNames] = useState({});
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([listClassConfigurations(), subjectApi.list(), academicYearApi.list()]).then(([configs, subjects, years]) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
      const map = {};
      subjects.forEach((s) => (map[s.id] = s.name));
      setSubjectNames(map);
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
    listGroupConfigurationsByClassId(cc.classId).then((list) => {
      setGroups(list);
      setGroupId(list.length > 0 ? String(list[0].groupId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId) {
      setSubjectConfigs([]);
      return;
    }
    listSubjectConfigs(examConfigId).then((list) => {
      setSubjectConfigs(list);
      setSubjectId(list.length > 0 ? String(list[0].subjectId) : "");
    });
  }, [examConfigId]);

  const examName = (examConfigs.find((e) => String(e.examConfigId) === examConfigId) || {}).examName || "";
  const sectionName = (classConfigs.find((c) => String(c.id) === classConfigurationId) || {}).classConfigName || "";
  const subjectName = subjectNames[subjectId] || "";

  const load = () => {
    if (!examConfigId || !subjectId || !classConfigurationId || !groupId || !academicYear) {
      setRows([]);
      return;
    }
    setLoading(true);
    getExamMarkGrid(examConfigId, subjectId, classConfigurationId, groupId, academicYear)
      .then((list) => {
        setRows(list);
        setSelectedIds([]);
      })
      .catch(() => setError("Failed to load marks."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [examConfigId, subjectId, classConfigurationId, groupId, academicYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(selectedIds.length === rows.length ? [] : rows.map((r) => r.identificationId));

  const handleSend = async () => {
    setError("");
    if (selectedIds.length === 0) {
      setError("Select at least one student.");
      return;
    }
    setSending(true);
    try {
      const lines = rows
        .filter((r) => selectedIds.includes(r.identificationId))
        .map((r) => ({
          identificationId: r.identificationId,
          message: `Name: ${r.studentName}, Class: ${sectionName}, Roll: ${r.studentRoll}, Exam Name: ${examName}, Subject Name: ${subjectName}, Obtained Marks: ${r.totalScore}, Grade: ${r.letterGrade}. ${instituteName}`,
        }));
      const res = await sendCustomSms({ lines });
      setSuccess(`${res.message} (${res.data.recipientCount} recipient(s), ${res.data.smsPartsUsed} part(s), balance now ${res.data.remainingBalance})`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to send SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            <option value="">Select Exam</option>
            {examConfigs.map((e) => (
              <option key={e.examConfigId} value={e.examConfigId}>
                {e.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Subject
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
            {subjectConfigs.length === 0 && <option value="">No subjects configured</option>}
            {subjectConfigs.map((sc) => (
              <option key={sc.subjectId} value={sc.subjectId}>
                {subjectNames[sc.subjectId] || sc.subjectId}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            {
              key: "select",
              label: <input type="checkbox" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleAll} />,
              render: (r) => <input type="checkbox" checked={selectedIds.includes(r.identificationId)} onChange={() => toggle(r.identificationId)} />,
            },
            { key: "studentRoll", label: "Roll" },
            { key: "studentName", label: "Student" },
            { key: "customStudentId", label: "Student ID" },
            { key: "totalScore", label: "Obtained Marks" },
            { key: "letterGrade", label: "Grade" },
          ]}
          rows={rows}
          emptyMessage="No marks found for this subject/section/group/year."
        />
      )}

      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending..." : "Send Subject Result SMS"}
        </button>
      </div>
    </>
  );
}

function TemplatesTab({ setError, setSuccess }) {
  const [sub, setSub] = useState("general");
  return (
    <>
      <Tabs
        tabs={[
          { key: "general", label: "General Templates" },
          { key: "attendance", label: "Attendance Templates" },
        ]}
        active={sub}
        onChange={setSub}
      />
      {sub === "general" && <GeneralTemplatesPanel setError={setError} setSuccess={setSuccess} />}
      {sub === "attendance" && <AttendanceTemplatesPanel setError={setError} setSuccess={setSuccess} />}
    </>
  );
}

function GeneralTemplatesPanel({ setError, setSuccess }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const load = () => {
    setLoading(true);
    listSmsTemplates()
      .then(setTemplates)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createSmsTemplate({ templateTitle: title, templateBody: body });
      setSuccess("Template saved.");
      setTitle("");
      setBody("");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save template.");
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.templateTitle}"?`)) return;
    try {
      await deleteSmsTemplate(t.templateId);
      setSuccess("Template deleted.");
      setTemplates((prev) => prev.filter((x) => x.templateId !== t.templateId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="user-form" style={{ marginTop: 16, marginBottom: 24 }}>
        <label>Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label>Body</label>
        <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} required />
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save Template
          </button>
        </div>
      </form>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "templateTitle", label: "Title" },
            { key: "templateBody", label: "Body" },
          ]}
          rows={templates}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </>
  );
}

function AttendanceTemplatesPanel({ setError, setSuccess }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [smsType, setSmsType] = useState(1);

  const load = () => {
    setLoading(true);
    listAttendanceSmsTemplates()
      .then(setTemplates)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createAttendanceSmsTemplate({ templateTitle: title, templateBody: body, smsType: Number(smsType) });
      setSuccess("Template saved.");
      setTitle("");
      setBody("");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save template.");
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.templateTitle}"?`)) return;
    try {
      await deleteAttendanceSmsTemplate(t.templateId);
      setSuccess("Template deleted.");
      setTemplates((prev) => prev.filter((x) => x.templateId !== t.templateId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="user-form" style={{ marginTop: 16, marginBottom: 24 }}>
        <label>Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label>Type</label>
        <select value={smsType} onChange={(e) => setSmsType(e.target.value)}>
          <option value={1}>Absent</option>
          <option value={2}>Present</option>
        </select>
        <label>Body (placeholders: {"{studentName} {instituteType} {period} {date}"})</label>
        <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} required />
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save Template
          </button>
        </div>
      </form>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "templateTitle", label: "Title" },
            { key: "smsTypeLabel", label: "Type" },
            { key: "templateBody", label: "Body" },
          ]}
          rows={templates}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </>
  );
}

function BalanceTab({ setError, setSuccess }) {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [allBalances, setAllBalances] = useState([]);
  const [instituteId, setInstituteId] = useState("");
  const [grantAmount, setGrantAmount] = useState("");
  const [trxId, setTrxId] = useState("");

  const load = () => {
    getSmsBalance().then(setBalance);
    if (isPlatformOperator(user)) {
      getAllSmsBalances().then(setAllBalances);
    }
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGive = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await giveSmsBalance({ instituteId: Number(instituteId), balance: Number(grantAmount), transactionId: trxId });
      setSuccess("Balance granted.");
      setGrantAmount("");
      setTrxId("");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to grant balance.");
    }
  };

  return (
    <>
      {balance && (
        <div style={{ marginBottom: 24 }}>
          <h2>{balance.instituteName}</h2>
          <p>
            <strong>Current Balance: {balance.balance} SMS</strong> &nbsp; (Rate: {balance.smsRate} Taka/SMS)
          </p>
        </div>
      )}

      {isPlatformOperator(user) && (
        <>
          <h3>Grant Balance (Platform Operator)</h3>
          <form onSubmit={handleGive} className="user-form" style={{ marginBottom: 24 }}>
            <div className="attendance-filters">
              <label>
                Institute ID
                <input value={instituteId} onChange={(e) => setInstituteId(e.target.value)} required style={{ width: 100 }} />
              </label>
              <label>
                SMS Amount
                <input type="number" value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} required style={{ width: 100 }} />
              </label>
              <label>
                Reference (optional)
                <input value={trxId} onChange={(e) => setTrxId(e.target.value)} />
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                Grant Balance
              </button>
            </div>
          </form>

          <h3>All Institutes</h3>
          <DataTable
            columns={[
              { key: "instituteId", label: "Institute ID" },
              { key: "instituteName", label: "Institute" },
              { key: "balance", label: "Balance" },
              { key: "smsRate", label: "Rate" },
            ]}
            rows={allBalances}
          />
        </>
      )}
    </>
  );
}

function ReportsTab({ setError }) {
  const [sub, setSub] = useState("log");
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    const request = sub === "log" ? getSmsLog(fromDate, toDate) : getBalanceGivenLog(fromDate, toDate);
    request
      .then(setRows)
      .catch(() => setError("Failed to load report."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [sub, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="attendance-filters">
        <label>
          Report
          <select value={sub} onChange={(e) => setSub(e.target.value)}>
            <option value="log">SMS Send Log</option>
            <option value="balance-log">Balance Grant History</option>
          </select>
        </label>
        <label>
          From
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : sub === "log" ? (
        <DataTable
          columns={[
            { key: "sendDate", label: "Date" },
            { key: "recipientType", label: "Type" },
            { key: "name", label: "Name" },
            { key: "toNumber", label: "Number" },
            { key: "smsBody", label: "Message" },
            { key: "smsCount", label: "Parts" },
          ]}
          rows={rows}
        />
      ) : (
        <DataTable
          columns={[
            { key: "givenDate", label: "Date" },
            { key: "totalSms", label: "SMS Granted" },
            { key: "givenBy", label: "By" },
            { key: "trxId", label: "Reference" },
          ]}
          rows={rows}
        />
      )}
    </>
  );
}
